import { useEffect, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { VENDORED_DEPENDENCY_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../shared/Locator';
import { emptyConclusionData } from '../../../../../shared/types/licenses';
import { useQuery } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { useCurrentProject } from '../../../../hooks/api/projects/useCurrentProject';
import { useRejectSnippets, useSnippetById, useUnrejectSnippets } from '../../../../hooks/api/snippets';
import { nuc, openInNewTab } from '../../../../utils';
import FallbackImage from '../../../generic/FallbackImage';
import Tabs, { useTabs } from '../../../generic/Tabs';
import DependencyLicenses from '../../dependenciesV2/DependencyDrawer/content/DependencyLicenses/DependencyLicenses';
import ModalSnippetConversion from '../convert/ModalSnippetConversion';
import useModalSnippetConversion from '../convert/useModalSnippetConversion';
import { verifySnippetDetailsForConversion } from '../convert/utils';
import useComparedSnippetsInfo from '../hooks/useComparedSnippetsInfo';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';

import Buttons from './Buttons';
import Code from './Code/Code';
import Details from './Details';
import Header from './Header';
import Loading from './Loading';
import RejectedBanner from './RejectedBanner';
import { SnippetLabels } from './SnippetLabels';

type DrawerTab = 'Details' | 'Licenses' | 'Compare Code' | 'Labels';

const tabIds: Readonly<{ tabIds: DrawerTab[] }> = {
  tabIds: ['Details', 'Licenses', 'Compare Code', 'Labels'],
};

type Props = {
  /**
   * This is required to either fetch or look up the snippet info from ReactQuery
   */
  userRevisionLocator: string;
};

/**
 * Content for the snippet drawer when opened with the Dresser pattern.
 * Contains a header with some info, and tabs for switching between
 * the "details" view and the code view.
 */
const SnippetsDrawerContent: VoidFunctionComponent<Props> = ({ userRevisionLocator }) => {
  const [queryParams] = useQuery<SnippetsQueryParams>();
  const { tabs, activeTab, resetTab } = useTabs<DrawerTab>(tabIds);

  // Track which path is currently selected in the code view.
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const comparedSnippetsInfo = useComparedSnippetsInfo();

  // If we are comparing snippets and the status is "removed", then we need to look up the snippet
  // in the older user revision since it won't exist in the current one. This also applies to
  // [un]rejecting snippets because we fetch them from Sparkle based on the user revision.
  const isComparingRemovedSnippets = comparedSnippetsInfo.isComparing && comparedSnippetsInfo.status === 'removed';
  const relevantUserRevisionLocator = isComparingRemovedSnippets
    ? comparedSnippetsInfo.olderRevisionLocator
    : userRevisionLocator;

  // Fetch this individual snippet by its ID.
  // This API call will only be made if queryParams.selectedSnippetId is defined.
  const { data, isLoading } = useSnippetById(
    relevantUserRevisionLocator,
    queryParams.selectedSnippetId as string,
    queryParams.path,
    !!queryParams.selectedSnippetId,
  );
  const snippet = data?.snippet;
  const selectedSnippetMatch = snippet?.matches.find(match => match.path === selectedPath);

  // snippet conversion button only shown when features enabled and
  // might be displayed disabled when snippet cannot be converted
  const shouldShowConversionButton = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const modalSnippetConversion = useModalSnippetConversion();
  const { shouldDisableConversion, disabledConversionMessage } = verifySnippetDetailsForConversion(snippet);

  const { data: project } = useCurrentProject();
  const isLicenseScanningEnabled = project?.snippetLicensingIssueScanningEnabled;

  // Depending on which view we are in (details vs compare code), we want to check
  // the rejection status of either the snippet or the selected match to determine
  // if the "Reject" or "Stop Rejecting" button should be shown.
  const isSnippetRejected = activeTab !== 'Compare Code' && !!snippet?.rejectionDetails;
  const isMatchRejected = activeTab === 'Compare Code' && !!selectedSnippetMatch?.rejectionDetails;
  const isRejected = isSnippetRejected || isMatchRejected;

  // Ensure we invalidate the current revision's snippets list since
  // [un]rejections target the older revision when comparing removed snippets.
  const { mutate: rejectSnippet, isPending: isRejecting } = useRejectSnippets(
    relevantUserRevisionLocator,
    { revisionLocator: userRevisionLocator },
  );
  const { mutate: unrejectSnippet, isPending: isUnrejecting } = useUnrejectSnippets(
    relevantUserRevisionLocator,
    { revisionLocator: userRevisionLocator },
  );

  //  Reset to the Details tab if the drawer is closed
  useEffect(() => {
    if (!queryParams.selectedSnippetId) {
      resetTab();
    }
  }, [queryParams.selectedSnippetId, resetTab]);

  //  If there is just no snippetID (but we haven't tried to load it) let's just show the loader
  if (isLoading || !queryParams.selectedSnippetId) {
    return <Loading />;
  }

  //  If there is no snippet to display (if it isn't in the list of `snippets`), display a fallback UI
  if (!snippet) {
    return (
      <FallbackImage
        src="no-results"
        alt="Snippet not found"
        hasPadding
      >
        Snippet Not Found
      </FallbackImage>
    );
  }

  return (
    <>
      <Header snippet={snippet} />
      <Buttons
        isRejected={isRejected}
        isRejecting={isRejecting}
        shouldShowConversionButton={shouldShowConversionButton}
        shouldDisableConversionButton={shouldDisableConversion}
        conversionButtonDisableMessage={disabledConversionMessage}
        onRejectClick={() => rejectSnippet({
          ids: [snippet.id],
          //  If viewing the details of a specific snippet path, use that path.
          //  Otherwise, use whichever path is currently selected in the parent tree view.
          //  If we are in the issue details drawer (after clicking View Details to "transform" it into the snippets drawer),
          //  we simply reject it everywhere (via the root "/").
          path: selectedPath ?? queryParams.path ?? '/',
        })}
        onConvertClick={() => {
          modalSnippetConversion.convertSnippetDetails(snippet);
        }}
        isUnrejecting={isUnrejecting}
        onUnrejectClick={() => unrejectSnippet({
          ids: [snippet.id],
          // If viewing the details of a specific snippet path,
          // Otherwise, use whichever path is currently selected in the parent tree view.
          path: selectedPath ?? queryParams.path ?? '/',
        })}
        onDownloadClick={() => {
          return openInNewTab(`/api/revisions/${nuc(relevantUserRevisionLocator)}/snippets/${nuc(snippet.id)}`);
        }}
      />
      {/* When NOT comparing code, use the snippet's rejection details */}
      {activeTab !== 'Compare Code' && snippet?.rejectionDetails && (
        <RejectedBanner rejectionDetails={snippet.rejectionDetails} rejectionTarget="snippet" />
      )}
      {/* When comparing code, use the selected snippet match's rejection details */}
      {activeTab === 'Compare Code' && selectedSnippetMatch?.rejectionDetails && (
        <RejectedBanner rejectionDetails={selectedSnippetMatch.rejectionDetails} rejectionTarget="match" />
      )}
      <Tabs>
        {tabs.map(tab => (
          <Tabs.Tab
            key={tab.label}
            // If the snippet doesn't have a locator, disable the Labels and Licenses tabs
            isDisabled={(tab.label === 'Labels' || tab.label === 'Licenses') && !snippet.locator}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...tab}
          />
        ))}
      </Tabs>
      {activeTab === 'Details' && <Details snippet={snippet} />}
      {activeTab === 'Licenses' && snippet.locator && (
        <DependencyLicenses
          issues={snippet.issues}
          licenseGroups={snippet.licenseGroups}
          concludedLicenses={snippet.conclusions ?? emptyConclusionData}
          scope={{
            dependencyRevisionLocator: snippet.locator,
            type: 'project',
            projectLocator: Locator.parsePackageString(userRevisionLocator),
            revisionLocator: userRevisionLocator,
          }}
          isLoading={false}
          dependency={{
            title: snippet.packageId,
            version: snippet.version,
            locator: snippet.locator,
          }}
          shouldAllowEditing
          isLicenseScanningEnabled={isLicenseScanningEnabled}
        />
      )}
      {activeTab === 'Compare Code' && (
        <Code
          userRevisionLocator={relevantUserRevisionLocator}
          selectedSnippetId={queryParams.selectedSnippetId}
          matches={snippet.matches}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
        />
      )}
      {activeTab === 'Labels' && snippet.locator && (
        <SnippetLabels
          snippetLocator={snippet.locator}
          snippetTitle={`${snippet.package} ${snippet.version}`}
          parentRevisionLocator={relevantUserRevisionLocator}
        />
      )}
      <ModalSnippetConversion
        userRevisionLocator={relevantUserRevisionLocator}
        snippetConversion={modalSnippetConversion.snippetConversion}
        setSnippetConversion={modalSnippetConversion.setSnippetConversion}
      />
    </>
  );
};

export default SnippetsDrawerContent;
